/**
 * Collection management utility for ceSDK editor uploads.
 * Handles finding or creating the "Editor Uploads" collection.
 * Identifies collection by integrationType, not by name.
 */

import { skedApi } from "../api/_skedApi";
import { getCollections } from "../api/media-library/get-collections";
import { localStorageFeatureToggle, FeatureName } from "./feature-toggle-utils";
import { MediaCollection } from "../api/media-library/types";

const EDITOR_UPLOADS_COLLECTION_NAME = "Editor Uploads";
const EDITOR_UPLOADS_INTEGRATION_TYPE = "cesdk-editor-uploads";
let cachedCollectionId: string | null = null;
let pendingPromise: Promise<string> | null = null;

/**
 * Get or create the "Editor Uploads" collection.
 * Identifies collection by integrationType, not by name.
 * Caches the collection ID to avoid repeated API calls.
 * Uses a pending promise to prevent race conditions.
 * @returns The collection ID (_id field)
 */
export async function getOrCreateEditorUploadsCollection(): Promise<string> {
  if (!localStorageFeatureToggle.get(FeatureName.CESDKEditorUploads)) {
    throw new Error(
      "Editor Uploads collection feature is disabled. Please enable the feature toggle."
    );
  }

  // Return cached ID if available
  if (cachedCollectionId) {
    return cachedCollectionId;
  }

  // Return pending promise if already in progress (prevents race condition)
  if (pendingPromise) {
    return pendingPromise;
  }

  // Create and store the promise to prevent concurrent requests
  pendingPromise = (async () => {
    try {
      // Get all collections
      const collections = await getCollections();

      // Search for collection by integrationType
      const existingCollection = collections.find(
        (collection) =>
          collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE
      );

      if (existingCollection) {
        cachedCollectionId = existingCollection._id;
        return cachedCollectionId;
      }

      // Collection doesn't exist, create it with integrationType
      const response = await skedApi.post<{ _id: string }>(
        "/content-inbox/inbox",
        {
          name: EDITOR_UPLOADS_COLLECTION_NAME,
          integrationType: EDITOR_UPLOADS_INTEGRATION_TYPE,
        }
      );

      if (!response.data?._id) {
        throw new Error(
          `Failed to create Editor Uploads collection: missing _id in response. Received: ${JSON.stringify(response.data)}`
        );
      }

      cachedCollectionId = response.data._id;
      return cachedCollectionId;
    } catch (error) {
      console.error(
        "Failed to get or create Editor Uploads collection:",
        error
      );
      throw error;
    } finally {
      pendingPromise = null;
    }
  })();

  return pendingPromise;
}

/**
 * Get the cached Editor Uploads collection ID.
 * Returns null if collection hasn't been initialized yet.
 * @returns The collection ID or null
 */
export async function getEditorUploadsCollectionId(): Promise<string | null> {
  if (cachedCollectionId) {
    return cachedCollectionId;
  }

  try {
    // Try to find existing collection by integrationType
    const collections = await getCollections();
    const existingCollection = collections.find(
      (collection) =>
        collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE
    );

    if (existingCollection) {
      cachedCollectionId = existingCollection._id;
      return cachedCollectionId;
    }

    return null;
  } catch (error) {
    console.error("Failed to get Editor Uploads collection ID:", error);
    return null;
  }
}

/**
 * Check if a collection is the Editor Uploads collection.
 * @param collection The collection to check
 * @returns true if this is the Editor Uploads collection
 */
export function isEditorUploadsCollection(
  collection: Pick<MediaCollection, "integrationType">
): boolean {
  return collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE;
}

/**
 * Clear the cached collection ID and pending promise.
 * Useful for testing or when the collection might have been deleted.
 */
export function clearEditorUploadsCollectionCache(): void {
  cachedCollectionId = null;
  pendingPromise = null;
}

/**
 * Delete a file from the Editor Uploads collection
 * @param fileId - The asset ID to delete
 */
export async function deleteEditorUploadsFile(fileId: string): Promise<void> {
  if (!localStorageFeatureToggle.get(FeatureName.CESDKEditorUploads)) {
    throw new Error("Editor Uploads deletion feature is disabled");
  }

  try {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { default: MediaLibraryLegacyApi } = await import(
      "../api/media-library/MediaLibraryLegacyApi"
    );
    const api = new MediaLibraryLegacyApi();
    await api.deleteEntry(fileId);
  } catch (error) {
    console.error("Failed to delete file from Editor Uploads:", error);
    throw new Error(
      `Failed to delete file: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
